/**
 * Signup Page
 * Create the household account: tenant + token (the same values used for
 * parking registration, shared by all family members). Parking API fields
 * are pre-filled with the server defaults and can be customized under
 * "Advanced".
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Row, Col, Collapse } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { formatTime12Hour, isValidTimeFormat } from '../utils/helpers';
import ParkingApiHelp from '../components/ParkingApiHelp';

const SignupPage = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();

  // All inputs start empty — the server applies your building's defaults
  // for any parking field left blank
  const [form, setForm] = useState({
    tenant: '',
    token: '',
    confirmToken: '',
    name: '',
    license_plate: '',
    phone_number: '',
    auto_register: true,
    pref_time: '',
    parking_api_url: '',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const validate = () => {
    const errs = {};
    if (!form.tenant.trim() || form.tenant.trim().length < 2) {
      errs.tenant = 'Tenant is required (min 2 characters)';
    }
    if (form.token.length < 4) {
      errs.token = 'Token must be at least 4 characters';
    }
    if (form.token !== form.confirmToken) {
      errs.confirmToken = 'Tokens do not match';
    }
    if (!form.name.trim()) {
      errs.name = 'Name is required';
    }
    if (!form.phone_number.trim()) {
      errs.phone_number = 'Phone number is required';
    }
    // Parking window is optional — the server uses defaults when empty;
    // but if provided, it must be valid
    if (form.pref_time && !isValidTimeFormat(form.pref_time)) {
      errs.pref_time = 'Invalid time format (HH:MM)';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      const { confirmToken, ...payload } = form;
      await signup(payload);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Signup failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center" style={{ marginTop: '2rem' }}>
      <Card style={{ width: '640px', maxWidth: '100%' }}>
        <Card.Header className="bg-dark text-white">
          <h5 className="mb-0"><i className="bi bi-person-plus me-2"></i>Create Your Household Account</h5>
        </Card.Header>
        <Card.Body>
          {submitError && <Alert variant="danger">{submitError}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label>Home Address *</Form.Label>
                  <Form.Control
                    type="text"
                    name="tenant"
                    value={form.tenant}
                    onChange={handleChange}
                    isInvalid={!!errors.tenant}
                    placeholder="e.g. 1463 Lavender Ln, Unit 10"
                    autoFocus
                  />
                  <Form.Control.Feedback type="invalid">{errors.tenant}</Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Identifies your household — also used for parking registration
                    (override in Advanced if your operator uses a different tenant name)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Your Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Token *</Form.Label>
                  <Form.Control
                    type="password"
                    name="token"
                    value={form.token}
                    onChange={handleChange}
                    isInvalid={!!errors.token}
                  />
                  <Form.Control.Feedback type="invalid">{errors.token}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Confirm Token *</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmToken"
                    value={form.confirmToken}
                    onChange={handleChange}
                    isInvalid={!!errors.confirmToken}
                  />
                  <Form.Control.Feedback type="invalid">{errors.confirmToken}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>License Plate</Form.Label>
                  <Form.Control
                    type="text"
                    name="license_plate"
                    value={form.license_plate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number (for permit SMS) *</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone_number"
                    value={form.phone_number}
                    onChange={handleChange}
                    isInvalid={!!errors.phone_number}
                  />
                  <Form.Control.Feedback type="invalid">{errors.phone_number}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Auto-register Time (HH:MM)</Form.Label>
                  <Form.Control
                    type="text"
                    name="pref_time"
                    value={form.pref_time}
                    onChange={handleChange}
                    isInvalid={!!errors.pref_time}
                    placeholder="19:00"
                  />
                  {errors.pref_time && (
                    <Form.Text className="text-danger small">{errors.pref_time}</Form.Text>
                  )}
                  <Form.Text className="text-muted d-block">
                    Leave empty to use the default (7:00 PM daily)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {isValidTimeFormat(form.pref_time) && (
              <Form.Text className="text-muted d-block mb-3">
                <i className="bi bi-clock me-1"></i>
                Daily at {formatTime12Hour(form.pref_time)}
              </Form.Text>
            )}

            <Form.Check
              type="switch"
              name="auto_register"
              id="signup-auto-register"
              label="Auto-register Parking"
              checked={form.auto_register}
              onChange={handleChange}
              className="mb-3"
            />

            <div className="border-top pt-3">
              <Button
                variant="link"
                className="p-0 mb-2"
                onClick={() => setShowAdvanced(!showAdvanced)}
                aria-expanded={showAdvanced}
              >
                <i className={`bi ${showAdvanced ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
                Advanced: custom parking registration API
              </Button>
              <Collapse in={showAdvanced}>
                <div>
                  <ParkingApiHelp />
                  <Form.Group className="mb-3">
                    <Form.Label>Registration API URL (DNS name)</Form.Label>
                    <Form.Control
                      type="text"
                      name="parking_api_url"
                      value={form.parking_api_url}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Other parameters (Location ID, Policy ID, Space, ...) default to your
                      building's values — you can review or extend them in Settings after signup.
                    </Form.Text>
                  </Form.Group>
                </div>
              </Collapse>
            </div>

            <Button variant="primary" type="submit" className="w-100 mt-3" disabled={submitting}>
              {submitting ? 'Creating account...' : 'Create Account'}
            </Button>
          </Form>

          <hr />

          <div className="text-center">
            <small>
              Already have an account? <Link to="/login">Log in</Link>
            </small>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SignupPage;
